import React from 'react';
import { Question } from '../types/testy';

interface QuestionStatus {
  answered: boolean;
  flagged: boolean;
}

interface NavigationGridProps {
  questions: Question[];
  questionStatuses: Record<string, QuestionStatus>;
  currentQuestionIndex: number;
  onQuestionSelect: (index: number) => void;
}

const NavigationGrid: React.FC<NavigationGridProps> = ({
  questions,
  questionStatuses,
  currentQuestionIndex,
  onQuestionSelect
}) => {
  // Get status counts for summary
  const getStatusCounts = () => {
    const answered = Object.values(questionStatuses).filter(status => status.answered).length;
    const flagged = Object.values(questionStatuses).filter(status => status.flagged).length;
    const unanswered = questions.length - answered;
    
    return { answered, flagged, unanswered };
  };

  const statusCounts = getStatusCounts();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 lg:p-6 sticky top-24">
      <h3 
        className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary mb-4 font-poppins"
        id="navigation-heading"
      >
        Navigace otázek
      </h3>
      
      {/* Status legend */}
      <div className="mb-6 space-y-3 text-sm" role="list" aria-label="Legenda stavů otázek">
        <div className="flex items-center" role="listitem">
          <div 
            className="w-4 h-4 bg-green-500 rounded mr-3"
            aria-hidden="true"
          ></div>
          <span className="text-light-text-secondary dark:text-dark-text-secondary">Zodpovězeno</span>
        </div>
        <div className="flex items-center" role="listitem">
          <div 
            className="w-4 h-4 bg-yellow-500 rounded mr-3"
            aria-hidden="true"
          ></div>
          <span className="text-light-text-secondary dark:text-dark-text-secondary">Označeno</span>
        </div>
        <div className="flex items-center" role="listitem">
          <div 
            className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded mr-3"
            aria-hidden="true"
          ></div>
          <span className="text-light-text-secondary dark:text-dark-text-secondary">Nezodpovězeno</span>
        </div>
      </div>

      {/* Question grid */}
      <div 
        className="grid grid-cols-6 sm:grid-cols-8 lg:grid-cols-4 xl:grid-cols-5 gap-2 mb-4"
        role="grid"
        aria-labelledby="navigation-heading"
        aria-describedby="navigation-description"
      >
        <div id="navigation-description" className="sr-only">
          Mřížka pro navigaci mezi otázkami. Použijte šipky nebo klikněte na číslo otázky pro přechod.
        </div>
        {questions.map((question, index) => {
          const status = questionStatuses[question.id];
          const isAnswered = status?.answered || false;
          const isFlagged = status?.flagged || false;
          const isCurrent = index === currentQuestionIndex;
          
          let statusText = 'nezodpovězeno';
          if (isAnswered) statusText = 'zodpovězeno';
          if (isFlagged) statusText += ', označeno';
          
          return (
            <button
              key={question.id}
              onClick={() => onQuestionSelect(index)}
              className={`
                w-9 h-9 sm:w-10 sm:h-10 lg:w-9 lg:h-9 xl:w-10 xl:h-10 rounded-md text-xs font-medium transition-all duration-200 relative focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800 hover:scale-105 active:scale-95
                ${isCurrent 
                  ? 'ring-2 ring-blue-500 ring-offset-1 ring-offset-white dark:ring-offset-gray-800 shadow-lg' 
                  : 'shadow-sm hover:shadow-md'
                }
                ${isAnswered 
                  ? 'bg-green-500 text-white hover:bg-green-600 focus:bg-green-600' 
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 focus:bg-gray-300 dark:focus:bg-gray-500'
                }
              `}
              aria-label={`Otázka ${index + 1}, ${statusText}${isCurrent ? ', aktuální' : ''}`}
              aria-current={isCurrent ? 'true' : 'false'}
              role="gridcell"
            >
              <span className="font-semibold">{index + 1}</span>
              {isFlagged && (
                <div 
                  className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-yellow-500 rounded-full border-2 border-white dark:border-gray-800 shadow-sm"
                  aria-hidden="true"
                  title="Označeno"
                ></div>
              )}
            </button>
          );
        })}
      </div>

      {/* Progress summary */}
      <div className="pt-6 border-t border-gray-200 dark:border-gray-600">
        <div className="text-sm space-y-2" role="region" aria-label="Souhrn pokroku">
          <div className="flex justify-between">
            <span className="text-light-text-secondary dark:text-dark-text-secondary">Celkem:</span>
            <span className="font-medium text-light-text-primary dark:text-dark-text-primary">{questions.length}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-light-text-secondary dark:text-dark-text-secondary">Zodpovězeno:</span>
            <span className="font-medium text-green-600 dark:text-green-400">{statusCounts.answered}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-light-text-secondary dark:text-dark-text-secondary">Označeno:</span>
            <span className="font-medium text-yellow-600 dark:text-yellow-400">{statusCounts.flagged}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-light-text-secondary dark:text-dark-text-secondary">Zbývá:</span>
            <span className="font-medium text-gray-600 dark:text-gray-400">{statusCounts.unanswered}</span>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-3">
          <div className="flex justify-between text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1">
            <span>Pokrok testu</span>
            <span>{Math.round((statusCounts.answered / questions.length) * 100)}%</span>
          </div>
          <div 
            className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3"
            role="progressbar"
            aria-valuenow={statusCounts.answered}
            aria-valuemin={0}
            aria-valuemax={questions.length}
            aria-label={`Pokrok testu: ${statusCounts.answered} z ${questions.length} otázek zodpovězeno, ${Math.round((statusCounts.answered / questions.length) * 100)} procent`}
          >
            <div 
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${(statusCounts.answered / questions.length) * 100}%` }}
              aria-hidden="true"
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavigationGrid;